import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Contact } from "@/types/crm";
import {
  User,
  Mail,
  Phone,
  Building2,
  Linkedin,
  Target,
  Activity,
  FileText,
  Calendar,
  DollarSign,
  Clock,
  Loader2,
  MessageSquare,
  Globe,
  Briefcase,
} from "lucide-react";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { opportunityAPI } from "@/services/opportunityService";
import { useAuth } from "@/hooks/useAuth";

interface ContactProfileProps {
  contact: Contact | null;
  isOpen: boolean;
  onClose: () => void;
}

function ContactProfile({ contact, isOpen, onClose }: ContactProfileProps) {
  const { company } = useAuth();
  const [opportunities, setOpportunities] = useState<any[]>([]);
  const [isLoadingOpps, setIsLoadingOpps] = useState(false);

  useEffect(() => {
    const fetchClientOpportunities = async () => {
      if (!contact?.id || !company?.slug || !isOpen) return;

      setIsLoadingOpps(true);
      try {
        const response = await opportunityAPI.getOpportunities({
          slug: company.slug,
          limit: 1000,
        });

        if (response.data) {
          const clientOpps = response.data.filter((opp: any) => {
            const oppContactId =
              typeof opp.contact === "object" ? opp.contact?.id : opp.contact;
            return oppContactId === contact.id || opp.contact_id === contact.id;
          });
          setOpportunities(clientOpps);
        }
      } catch (error) {
        console.error("Error fetching opportunities for contact:", error);
      } finally {
        setIsLoadingOpps(false);
      }
    };

    fetchClientOpportunities();
  }, [isOpen, contact?.id, company?.slug]);

  if (!contact) return null;

  const c = contact as any;

  const getNameParts = () => {
    if (contact.firstName && contact.lastName) {
      return { firstName: contact.firstName, lastName: contact.lastName };
    }
    const fullName = c.name || "";
    const parts = fullName.split(" ");
    if (parts.length > 0) {
      return { firstName: parts[0], lastName: parts.slice(1).join(" ") };
    }
    return { firstName: "", lastName: "" };
  };

  const { firstName, lastName } = getNameParts();

  const getInitials = () => {
    if (firstName && lastName) {
      return `${firstName[0]}${lastName[0]}`.toUpperCase();
    }
    if (firstName) {
      return firstName.substring(0, 2).toUpperCase();
    }
    return "??";
  };

  const companyName =
    contact.company?.name || c.client_company?.name || "Sin empresa";
  const createdAt = contact.createdAt || c.created_at;
  const updatedAt = contact.updatedAt || c.updated_at;
  const lastActivityAt = (contact as any).lastActivityAt || c.last_activity_at;

  const totalOpportunityValue = opportunities.reduce(
    (sum, opp) =>
      sum + (parseFloat(opp.estimated_value) || parseFloat(opp.value) || 0),
    0,
  );
  const wonOpportunities = opportunities.filter(
    (opp) => opp.stage?.stage_type === "closed_won" || opp.stage === "ganada",
  );
  const totalWonValue = wonOpportunities.reduce(
    (sum, opp) =>
      sum + (parseFloat(opp.estimated_value) || parseFloat(opp.value) || 0),
    0,
  );

  const formatSafeDate = (
    dateInput: string | Date | undefined,
    formatStr: string,
  ) => {
    if (!dateInput) return "No disponible";
    try {
      let date: Date;
      if (
        typeof dateInput === "string" &&
        dateInput.includes(" ") &&
        !dateInput.includes("T") &&
        !dateInput.includes("Z")
      ) {
        date = new Date(dateInput.replace(" ", "T") + "Z");
      } else {
        date = dateInput instanceof Date ? dateInput : new Date(dateInput);
      }
      if (isNaN(date.getTime())) return "Fecha inválida";
      return format(date, formatStr, { locale: es });
    } catch (error) {
      return "Fecha inválida";
    }
  };

  const formatRelativeTime = (dateString: string | undefined) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      const today = new Date();
      const diffTime = Math.abs(today.getTime() - date.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return `hace  días`;
    } catch (e) {
      return "";
    }
  };

  const formatCurrency = (value: number) => {
    if (value === 0) return "$0";
    if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `$${(value / 1000).toFixed(1)}K`;
    return `$${value.toLocaleString()}`;
  };

  const formatOpportunityValue = (value: number, currencyCode?: string) => {
    try {
      return new Intl.NumberFormat("es-ES", {
        style: "currency",
        currency: currencyCode || "USD",
        maximumFractionDigits: 2,
      }).format(value);
    } catch (e) {
      return `$${value.toLocaleString()}`;
    }
  };

  const getActivityIcon = (type: string) => {
    const normalizedType = type?.toLowerCase() || "";
    if (normalizedType.includes("call") || normalizedType.includes("llamada"))
      return <Phone className="h-5 w-5" />;
    if (normalizedType.includes("mail") || normalizedType.includes("correo"))
      return <Mail className="h-5 w-5" />;
    if (normalizedType.includes("meet") || normalizedType.includes("reun"))
      return <Calendar className="h-5 w-5" />;
    return <MessageSquare className="h-5 w-5" />;
  };

  const getActivityStats = () => {
    const acts = contact.activities || [];
    return {
      calls: acts.filter(
        (a) =>
          a.type?.toLowerCase().includes("call") ||
          a.type?.toLowerCase().includes("llamada"),
      ).length,
      emails: acts.filter(
        (a) =>
          a.type?.toLowerCase().includes("mail") ||
          a.type?.toLowerCase().includes("correo"),
      ).length,
      meetings: acts.filter(
        (a) =>
          a.type?.toLowerCase().includes("meet") ||
          a.type?.toLowerCase().includes("reun"),
      ).length,
    };
  };

  const stats = getActivityStats();

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-[95vw] sm:max-w-5xl max-h-[90vh] overflow-hidden p-0 custom-scrollbar rounded-[24px] border-slate-100 flex flex-col bg-slate-50">
        <div className="bg-white border-b border-slate-100 p-4 sm:p-5 sm:px-6 z-10 relative">
          <DialogHeader>
            <div className="flex items-center gap-4">
              <Avatar className="h-14 w-14 shadow-sm border-2 border-white ring-1 ring-slate-100">
                <AvatarImage src={contact.avatar} />
                <AvatarFallback className="bg-gradient-to-br from-indigo-500 to-purple-600 text-white text-xl font-bold shadow-inner">
                  {getInitials()}
                </AvatarFallback>
              </Avatar>
              <div className="text-left flex-1">
                <DialogTitle className="text-xl font-bold text-slate-900 tracking-tight">
                  {firstName} {lastName}
                </DialogTitle>
                <div className="flex flex-wrap items-center gap-2 mt-1.5">
                  <span className="inline-flex items-center text-xs font-medium text-slate-600 bg-slate-100 px-2 py-0.5 rounded-full">
                    <Briefcase className="h-3 w-3 mr-1 text-indigo-500" />
                    {contact.position || "Sin cargo"}
                  </span>
                  <span className="inline-flex items-center text-xs font-medium text-slate-600 bg-slate-100 px-2 py-0.5 rounded-full">
                    <Building2 className="h-3 w-3 mr-1 text-blue-500" />
                    {companyName}
                  </span>
                </div>
              </div>
            </div>
          </DialogHeader>
        </div>

        <div className="p-6 overflow-y-auto custom-scrollbar flex-1">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <div className="bg-white rounded-[20px] border border-slate-100 shadow-sm overflow-hidden">
                <div className="px-5 py-4 border-b border-slate-50 flex items-center">
                  <User className="h-5 w-5 mr-2 text-indigo-500" />
                  <h3 className="text-lg font-bold text-slate-800 tracking-tight">Información General</h3>
                </div>
                <div className="p-5 space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full">
                      <div className="space-y-4">
                        <div className="group flex items-start space-x-3">
                          <div className="bg-slate-50 group-hover:bg-indigo-50 transition-colors p-2.5 rounded-xl border border-slate-100 mt-0.5">
                            <Mail className="h-4 w-4 text-indigo-600" />
                          </div>
                          <div className="flex-1 overflow-hidden">
                            <h3 className="text-xs font-medium text-slate-500 mb-0.5 uppercase tracking-wider">
                              Email
                            </h3>
                            <span className="text-slate-800 font-medium leading-tight truncate block">
                              {contact.email}
                            </span>
                          </div>
                        </div>

                        <div className="group flex items-start space-x-3">
                          <div className="bg-slate-50 group-hover:bg-indigo-50 transition-colors p-2.5 rounded-xl border border-slate-100 mt-0.5">
                            <Phone className="h-4 w-4 text-indigo-600" />
                          </div>
                          <div className="flex-1 overflow-hidden">
                            <h3 className="text-xs font-medium text-slate-500 mb-0.5 uppercase tracking-wider">
                              Teléfono
                            </h3>
                            <span className="text-slate-800 font-medium leading-tight truncate block">
                              {contact.countryCode
                                ? `${contact.countryCode} ${contact.phone}`
                                : contact.phone || "No disponible"}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="space-y-4">
                        <div className="group flex items-start space-x-3">
                          <div className="bg-slate-50 group-hover:bg-indigo-50 transition-colors p-2.5 rounded-xl border border-slate-100 mt-0.5">
                            <Building2 className="h-4 w-4 text-indigo-600" />
                          </div>
                          <div className="flex-1 overflow-hidden">
                            <h3 className="text-xs font-medium text-slate-500 mb-0.5 uppercase tracking-wider">
                              Empresa
                            </h3>
                            <span className="text-slate-800 font-medium leading-tight truncate block">
                              {companyName}
                            </span>
                          </div>
                        </div>

                        {contact.linkedinUrl && (
                          <div className="group flex items-start space-x-3">
                            <div className="bg-slate-50 group-hover:bg-indigo-50 transition-colors p-2.5 rounded-xl border border-slate-100 mt-0.5">
                              <Linkedin className="h-4 w-4 text-indigo-600" />
                            </div>
                            <div className="flex-1 overflow-hidden">
                              <h3 className="text-xs font-medium text-slate-500 mb-0.5 uppercase tracking-wider">
                                Redes Sociales
                              </h3>
                              <a
                                href={contact.linkedinUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-indigo-600 hover:text-indigo-800 hover:underline font-medium leading-tight truncate block"
                              >
                                Ver perfil de LinkedIn
                              </a>
                            </div>
                          </div>
                        )}

                        {(contact as any).communication_preferences &&
                          (contact as any).communication_preferences.length > 0 && (
                            <div className="group flex items-start space-x-3">
                              <div className="bg-slate-50 group-hover:bg-indigo-50 transition-colors p-2.5 rounded-xl border border-slate-100 mt-0.5">
                                <Globe className="h-4 w-4 text-indigo-600" />
                              </div>
                              <div className="flex-1 overflow-hidden space-y-2">
                                <h3 className="text-xs font-medium text-slate-500 mb-0.5 uppercase tracking-wider">
                                  Canales de Comunicación
                                </h3>
                                {(contact as any).communication_preferences.map(
                                  (pref: any, idx: number) => {
                                    const val =
                                      typeof pref === "string"
                                        ? pref
                                        : pref.value || pref.url;
                                    return (
                                      <a
                                        key={idx}
                                        href={val}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-indigo-600 hover:text-indigo-800 hover:underline font-medium leading-tight truncate block"
                                      >
                                        {val}
                                      </a>
                                    );
                                  },
                                )}
                              </div>
                            </div>
                          )}
                      </div>
                    </div>

                  {contact.notes && (
                    <div className="pt-4 border-t border-slate-50">
                      <div className="group flex items-start space-x-3">
                        <div className="bg-slate-50 group-hover:bg-indigo-50 transition-colors p-2.5 rounded-xl border border-slate-100 mt-0.5">
                          <FileText className="h-4 w-4 text-indigo-600" />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-xs font-medium text-slate-500 mb-2 uppercase tracking-wider">
                            Notas
                          </h3>
                          <div className="bg-amber-50/50 border border-amber-100/50 p-4 rounded-xl">
                            <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">
                              {contact.notes}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-white rounded-[20px] border border-slate-100 shadow-sm overflow-hidden">
                <div className="px-5 py-4 border-b border-slate-50 flex items-center">
                  <Target className="h-5 w-5 mr-2 text-indigo-500" />
                  <h3 className="text-lg font-bold text-slate-800 tracking-tight">
                    Oportunidades ({opportunities.length})
                  </h3>
                </div>
                <div className="p-5">
                  {isLoadingOpps ? (
                    <div className="flex justify-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin text-indigo-600" />
                    </div>
                  ) : opportunities.length > 0 ? (
                    <div className="space-y-4">
                      {opportunities.map((opportunity) => (
                        <div
                          key={opportunity.id}
                          className="flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-slate-50/50 hover:bg-indigo-50/30 border border-slate-100/50 rounded-xl transition-colors gap-4"
                        >
                          <div className="flex items-center space-x-4">
                            <div className="p-2.5 bg-white border border-slate-100 shadow-sm rounded-xl">
                              <Target className="h-5 w-5 text-indigo-600" />
                            </div>
                            <div>
                              <p className="font-semibold text-slate-800 leading-tight">
                                {opportunity.title}
                              </p>
                              <div className="flex flex-wrap items-center text-sm text-slate-500 mt-1.5 gap-3">
                                <span className="flex items-center">
                                  <Calendar className="h-3.5 w-3.5 mr-1" />{" "}
                                  {formatSafeDate(
                                    opportunity.estimated_close_date ||
                                    opportunity.expectedCloseDate,
                                    "dd MMM yyyy",
                                  )}
                                </span>
                                <span className="flex items-center font-medium text-slate-600">
                                  {formatOpportunityValue(
                                    parseFloat(
                                      opportunity.estimated_value ||
                                      opportunity.value ||
                                      0,
                                    ),
                                    opportunity.currency?.code ||
                                    opportunity.currency_code,
                                  )}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="sm:text-right flex flex-row sm:flex-col items-center sm:items-end justify-between sm:justify-center w-full sm:w-auto">
                            <Badge variant="outline" className="mb-1 bg-white border-slate-200 text-slate-700">
                              {opportunity.stage?.name ||
                                opportunity.stage ||
                                "Sin etapa"}
                            </Badge>
                            <p className="text-xs font-medium text-slate-500 bg-slate-100 px-2 py-0.5 rounded-md">
                              {opportunity.success_probability ||
                                opportunity.probability ||
                                0}
                              % prob.
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-slate-500 bg-slate-50/50 rounded-xl border border-slate-100/50">
                      No hay oportunidades registradas
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-white rounded-[20px] border border-slate-100 shadow-sm overflow-hidden">
                <div className="px-5 py-4 border-b border-slate-50 flex items-center">
                  <Activity className="h-5 w-5 mr-2 text-indigo-500" />
                  <h3 className="text-lg font-bold text-slate-800 tracking-tight">Actividades Recientes</h3>
                </div>
                <div className="p-5">
                  {contact.activities && contact.activities.length > 0 && (
                    <div className="grid grid-cols-3 gap-4 mb-6">
                      <div className="flex flex-col items-center justify-center p-3 bg-blue-50/50 rounded-xl border border-blue-100">
                        <Phone className="h-5 w-5 text-blue-600 mb-1" />
                        <span className="text-xs font-medium text-slate-500">
                          Llamadas
                        </span>
                        <span className="text-lg font-bold text-slate-900">
                          {stats.calls}
                        </span>
                      </div>
                      <div className="flex flex-col items-center justify-center p-3 bg-orange-50/50 rounded-xl border border-orange-100">
                        <Mail className="h-5 w-5 text-orange-600 mb-1" />
                        <span className="text-xs font-medium text-slate-500">
                          Correos
                        </span>
                        <span className="text-lg font-bold text-slate-900">
                          {stats.emails}
                        </span>
                      </div>
                      <div className="flex flex-col items-center justify-center p-3 bg-purple-50/50 rounded-xl border border-purple-100">
                        <Calendar className="h-5 w-5 text-purple-600 mb-1" />
                        <span className="text-xs font-medium text-slate-500">
                          Reuniones
                        </span>
                        <span className="text-lg font-bold text-slate-900">
                          {stats.meetings}
                        </span>
                      </div>
                    </div>
                  )}

                  {contact.activities && contact.activities.length > 0 ? (
                    <div className="space-y-6 relative before:absolute before:inset-0 before:ml-5 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-slate-300 before:to-transparent">
                      {contact.activities.map((activity) => (
                        <div
                          key={activity.id}
                          className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active"
                        >
                          <div className="flex items-center justify-center w-10 h-10 rounded-full border border-white bg-slate-50 group-[.is-active]:bg-purple-500 text-slate-500 group-[.is-active]:text-white shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2">
                            {getActivityIcon(activity.type)}
                          </div>
                          <div className="w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] p-4 rounded-xl border border-slate-200 bg-white shadow-sm">
                            <div className="flex items-center justify-between space-x-2 mb-1">
                              <div className="font-bold text-slate-900">
                                {activity.title}
                              </div>
                              <time className="font-caveat font-medium text-indigo-500 text-xs">
                                {formatSafeDate(activity.date, "dd MMM")}
                              </time>
                            </div>
                            <div className="text-slate-500 text-sm">
                              {activity.description}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-slate-500 bg-slate-50/50 rounded-xl border border-slate-100/50">
                      No hay actividades recientes
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <div className="bg-white rounded-[20px] border border-slate-100 shadow-sm overflow-hidden">
                <div className="px-5 py-4 border-b border-slate-50 flex items-center">
                  <DollarSign className="h-5 w-5 mr-2 text-indigo-500" />
                  <h3 className="text-lg font-bold text-slate-800 tracking-tight">Métricas</h3>
                </div>
                <div className="p-5 space-y-4">
                  <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
                    <p className="text-sm text-blue-600 font-medium mb-1">
                      Pipeline Total
                    </p>
                    <p className="text-2xl font-bold text-blue-900">
                      {formatCurrency(totalOpportunityValue)}
                    </p>
                  </div>
                  <div className="p-4 bg-green-50 rounded-lg border border-green-100">
                    <p className="text-sm text-green-600 font-medium mb-1">
                      Ganado
                    </p>
                    <p className="text-2xl font-bold text-green-900">
                      {formatCurrency(totalWonValue)}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-[20px] border border-slate-100 shadow-sm overflow-hidden">
                <div className="px-5 py-4 border-b border-slate-50 flex items-center">
                  <Clock className="h-5 w-5 mr-2 text-indigo-500" />
                  <h3 className="text-lg font-bold text-slate-800 tracking-tight">Información del Sistema</h3>
                </div>
                <div className="p-5 space-y-3">
                  <div>
                    <label className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                      Creado el
                    </label>
                    <div className="flex items-center text-sm text-slate-900 mt-1">
                      <Calendar className="h-4 w-4 mr-2 text-slate-400" />
                      {formatSafeDate(createdAt, "dd MMM yyyy HH:mm")}
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                      Última actualización
                    </label>
                    <div className="flex items-center text-sm text-slate-900 mt-1">
                      <Activity className="h-4 w-4 mr-2 text-slate-400" />
                      {formatSafeDate(updatedAt, "dd MMM yyyy HH:mm")}
                    </div>
                  </div>
                  {lastActivityAt && (
                    <div>
                      <label className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                        Última actividad
                      </label>
                      <div className="flex items-center text-sm text-slate-900 mt-1">
                        <Clock className="h-4 w-4 mr-2 text-slate-400" />
                        {formatSafeDate(lastActivityAt, "dd MMM yyyy HH:mm")}
                        <span className="text-slate-400 ml-2 text-xs">
                          ({formatRelativeTime(lastActivityAt)})
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white border-t border-slate-100 p-5 px-6 rounded-b-[24px] flex-shrink-0 z-10 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.02)]">
          <div className="flex justify-end w-full">
            <Button variant="outline" onClick={onClose} className="min-w-[100px] h-10 rounded-lg">
              Cerrar
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export { ContactProfile };
